import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Clock,
  Building2,
  CheckCircle2,
  PauseCircle,
  Loader2,
  Edit,
  Play,
  Pause,
  BarChart3,
  Calendar,
  MessageSquare,
  Settings,
  User,
  FileText,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  active: { label: 'Active', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  onboarding: { label: 'Onboarding', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  paused: { label: 'Paused', icon: PauseCircle, color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30' }
};

export default function ClientDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers/${customerId}`);
      setCustomer(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to load customer');
      navigate('/admin/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/customers/${customerId}/activate`);
      toast.success('Customer activated!');
      fetchCustomer();
    } catch (error) {
      console.error('Error activating customer:', error);
      toast.error('Failed to activate customer');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeactivate = async () => {
    setUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/customers/${customerId}/deactivate`);
      toast.success('Customer paused');
      fetchCustomer();
    } catch (error) {
      console.error('Error deactivating customer:', error);
      toast.error('Failed to pause customer');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveChanges = async () => {
    setUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/customers/${customerId}`, editData);
      toast.success('Customer updated!');
      setEditMode(false);
      fetchCustomer();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Failed to update customer');
    } finally {
      setUpdating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  if (!customer) return null;

  const status = statusConfig[customer.status] || statusConfig.onboarding;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin/clients')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg">Alaska AI Services</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {customer.status === 'active' ? (
                <Button 
                  onClick={handleDeactivate}
                  disabled={updating}
                  variant="outline"
                  className="border-slate-700 text-slate-300"
                  data-testid="pause-btn"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4 mr-2" />}
                  Pause AI
                </Button>
              ) : (
                <Button 
                  onClick={handleActivate}
                  disabled={updating}
                  className="bg-green-500 hover:bg-green-600"
                  data-testid="activate-btn"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Activate AI
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Client Header Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-slate-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">{customer.business_name}</h1>
                <div className="flex items-center gap-3 text-slate-400">
                  <span>{customer.business_type_name || customer.business_type}</span>
                  <span>•</span>
                  <span>ID: {customer.customer_id}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${status.bg} border ${status.border}`}>
                <StatusIcon className={`w-5 h-5 ${status.color}`} />
                <span className={`font-medium ${status.color}`}>{status.label}</span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-800">
            <div className="text-center">
              <div className="text-2xl font-bold text-sky-400">{customer.leads_captured || 0}</div>
              <div className="text-sm text-slate-500">Leads Captured</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">{customer.appointments_booked || 0}</div>
              <div className="text-sm text-slate-500">Appointments</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">
                {customer.leads_captured > 0 
                  ? Math.round((customer.appointments_booked / customer.leads_captured) * 100) 
                  : 0}%
              </div>
              <div className="text-sm text-slate-500">Conversion Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {customer.created_at 
                  ? Math.floor((Date.now() - new Date(customer.created_at).getTime()) / (1000 * 60 * 60 * 24))
                  : 0}
              </div>
              <div className="text-sm text-slate-500">Days Active</div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: Building2 },
            { id: 'prompt', label: 'AI Prompt', icon: MessageSquare },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-sky-500 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid lg:grid-cols-2 gap-6"
          >
            {/* Contact Info */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-sky-400" />
                Contact Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Owner</span>
                  <span className="font-medium">{customer.owner_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Email</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{customer.email}</span>
                    <button onClick={() => copyToClipboard(customer.email)} className="p-1 hover:bg-slate-800 rounded">
                      <Copy className="w-3 h-3 text-slate-500" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Phone</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{customer.phone}</span>
                    <button onClick={() => copyToClipboard(customer.phone)} className="p-1 hover:bg-slate-800 rounded">
                      <Copy className="w-3 h-3 text-slate-500" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Info */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-sky-400" />
                Business Details
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Location</span>
                  <span className="font-medium">{customer.location}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Hours</span>
                  <span className="font-medium">{customer.business_hours}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Calendar</span>
                  <span className="font-medium capitalize">{customer.calendar_system || 'Not set'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Created</span>
                  <span className="font-medium">
                    {customer.created_at 
                      ? new Date(customer.created_at).toLocaleDateString()
                      : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {customer.custom_notes && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 lg:col-span-2">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-400" />
                  Custom Notes
                </h3>
                <p className="text-slate-300 whitespace-pre-wrap">{customer.custom_notes}</p>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'prompt' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-sky-400" />
                AI System Prompt
              </h3>
              <Button
                onClick={() => copyToClipboard(customer.system_prompt)}
                variant="outline"
                className="border-slate-700"
                data-testid="copy-prompt-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Prompt
              </Button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              This is the system prompt that powers the AI for this customer. It was generated from the {customer.business_type_name} template.
            </p>
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 font-mono text-sm text-slate-300 whitespace-pre-wrap max-h-[500px] overflow-y-auto">
              {customer.system_prompt}
            </div>
          </motion.div>
        )}

        {activeTab === 'settings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-sky-400" />
                Edit Customer
              </h3>
              {editMode ? (
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditMode(false);
                      setEditData(customer);
                    }}
                    variant="outline"
                    className="border-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={updating}
                    className="bg-sky-500 hover:bg-sky-600"
                    data-testid="save-changes-btn"
                  >
                    {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Changes
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setEditMode(true)}
                  variant="outline"
                  className="border-slate-700"
                  data-testid="edit-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>

            <div className="space-y-6 max-w-xl">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Business Name</label>
                <Input
                  value={editData.business_name || ''}
                  onChange={(e) => setEditData({ ...editData, business_name: e.target.value })}
                  disabled={!editMode}
                  className="bg-slate-800/50 border-slate-700 text-white disabled:opacity-60"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Owner Name</label>
                  <Input
                    value={editData.owner_name || ''}
                    onChange={(e) => setEditData({ ...editData, owner_name: e.target.value })}
                    disabled={!editMode}
                    className="bg-slate-800/50 border-slate-700 text-white disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <Input
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    disabled={!editMode}
                    className="bg-slate-800/50 border-slate-700 text-white disabled:opacity-60"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                  <Input
                    value={editData.phone || ''}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    disabled={!editMode}
                    className="bg-slate-800/50 border-slate-700 text-white disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
                  <Input
                    value={editData.location || ''}
                    onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                    disabled={!editMode}
                    className="bg-slate-800/50 border-slate-700 text-white disabled:opacity-60"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Business Hours</label>
                <Input
                  value={editData.business_hours || ''}
                  onChange={(e) => setEditData({ ...editData, business_hours: e.target.value })}
                  disabled={!editMode}
                  className="bg-slate-800/50 border-slate-700 text-white disabled:opacity-60"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Custom Notes</label>
                <Textarea
                  value={editData.custom_notes || ''}
                  onChange={(e) => setEditData({ ...editData, custom_notes: e.target.value })}
                  disabled={!editMode}
                  rows={4}
                  className="bg-slate-800/50 border-slate-700 text-white disabled:opacity-60"
                />
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
